import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Download, Trash2, CreditCard, Check, Crown, Mail, Send } from "lucide-react";
import { api, apiError } from "../api";
import { useAuth } from "../auth";
import { usePricing } from "../usePricing";
import { PageLoader, StatusBadge, Spinner, Toast, useToast } from "../components/ui";

const TIMEZONES = [
  "Indian/Mauritius", "Indian/Reunion", "Africa/Nairobi", "Asia/Kolkata",
  "Europe/London", "Europe/Paris", "Asia/Dubai", "UTC",
];

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const pricing = usePricing();
  const navigate = useNavigate();
  const { toast, show, close } = useToast();
  const [dash, setDash] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [tz, setTz] = useState(user?.timezone || "Indian/Mauritius");
  const [digest, setDigest] = useState(!!user?.daily_digest);
  const [digestHour, setDigestHour] = useState(user?.digest_hour ?? 8);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then((r) => setDash(r.data)).catch(() => setDash({ profiles_used: 0, max_profiles: 5 }));
  }, []);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setTz(user.timezone || "Indian/Mauritius");
      setDigest(!!user.daily_digest);
      setDigestHour(user.digest_hour ?? 8);
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/account", { name, timezone: tz, daily_digest: digest, digest_hour: digestHour });
      await refresh();
      show("Account updated.");
    } catch (e) {
      show(apiError(e), "error");
    } finally { setSaving(false); }
  };

  const sendTestDigest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/account/digest/test");
      show(data.message, data.ok ? "success" : "error");
    } catch (e) {
      show(apiError(e), "error");
    } finally { setBusy(false); }
  };

  const cancelSub = async () => {
    if (!window.confirm("Cancel your Pro subscription?")) return;
    setBusy(true);
    try {
      await api.post("/payments/cancel");
      await refresh();
      show("Subscription cancelled.");
    } catch (e) {
      show(apiError(e), "error");
    } finally { setBusy(false); }
  };

  const exportJson = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pfu-monitor-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      show("Export downloaded.");
    } catch (e) {
      show(apiError(e), "error");
    } finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    if (!window.confirm("Permanently delete your account and ALL data? This cannot be undone.")) return;
    try {
      await api.delete("/account");
      logout();
      navigate("/login");
    } catch (e) {
      show(apiError(e), "error");
    }
  };

  if (!dash) return <PageLoader label="Loading account" />;

  const onTrial = user?.subscription_status === "trialing" && user?.has_access;
  const trialDays = user?.trial_days_left ?? 0;
  const subStatus = user?.subscription_status === "active" ? "active"
    : onTrial ? "trialing"
    : user?.subscription_status === "cancelled" ? "cancelled_sub" : "inactive";

  return (
    <div className="fade-in pb-6">
      <Toast toast={toast} onClose={close} />
      <h1 className="font-display text-2xl md:text-3xl text-ink mb-6">Account</h1>

      {/* Plan */}
      <div className="card p-5 mb-4" data-testid="account-plan">
        <div className="flex items-center gap-2 mb-3">
          <Crown className="w-5 h-5 text-coral" />
          <h2 className="font-display text-lg text-ink">Plan</h2>
          <div className="ml-auto"><StatusBadge status={subStatus} /></div>
        </div>
        <div className="flex items-end gap-2 mb-1">
          <span className="font-display text-2xl font-bold text-ink">Pro</span>
          <span className="text-muted mb-0.5">· {pricing.display}/{pricing.period}</span>
        </div>
        {onTrial && (
          <p className="text-sm text-emerald font-medium mb-1" data-testid="account-trial">
            Free trial — {trialDays} day{trialDays === 1 ? "" : "s"} left
          </p>
        )}
        <p className="text-sm text-muted mb-4" data-testid="profiles-used">
          {dash.profiles_used} of {dash.max_profiles} profiles used
        </p>
        {user?.subscription_status === "active" ? (
          <button className="btn-ghost w-full" onClick={cancelSub} disabled={busy} data-testid="cancel-subscription">
            <CreditCard className="w-4 h-4" /> Cancel subscription
          </button>
        ) : (
          <button className="btn-accent w-full" onClick={() => navigate("/pricing")} data-testid="manage-subscription">
            <CreditCard className="w-4 h-4" /> {onTrial ? "Subscribe early" : user?.subscription_status === "cancelled" ? "Resubscribe" : "Subscribe"} — {pricing.display}/{pricing.period}
          </button>
        )}
      </div>

      {/* Profile info */}
      <div className="card p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-teal" />
          <h2 className="font-display text-lg text-ink">Profile</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="an">Name</label>
            <input id="an" className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name" />
          </div>
          <div>
            <label className="label" htmlFor="atz">Timezone</label>
            <select id="atz" className="input" value={tz} onChange={(e) => setTz(e.target.value)} data-testid="account-timezone">
              {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="text-sm text-muted">Email: <span className="text-ink">{user?.email}</span></div>
          <button className="btn-primary w-full" onClick={save} disabled={saving} data-testid="account-save">
            {saving ? <Spinner /> : <Check className="w-4 h-4" />} Save changes
          </button>
        </div>
      </div>

      {/* Email digest */}
      <div className="card p-5 mb-4" data-testid="digest-card">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-teal" />
          <h2 className="font-display text-lg text-ink">Email digest</h2>
        </div>
        <button
          onClick={() => setDigest((v) => !v)}
          className={`w-full flex items-center gap-3 p-3 rounded-card border mb-3 ${digest ? "border-teal bg-teal-light" : "border-stone-border"}`}
          data-testid="digest-toggle"
        >
          <Mail className={`w-5 h-5 ${digest ? "text-teal" : "text-muted"}`} />
          <span className="flex-1 text-left">
            <span className="block font-medium text-ink">Daily digest</span>
            <span className="block text-xs text-muted">Batch each morning's price changes into one email instead of per-scan alerts.</span>
          </span>
          {digest && <Check className="w-5 h-5 text-teal" />}
        </button>
        {digest && (
          <div className="mb-3">
            <label className="label" htmlFor="dh">Send at (your local time)</label>
            <select id="dh" className="input" value={digestHour} onChange={(e) => setDigestHour(Number(e.target.value))} data-testid="digest-hour">
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex gap-2">
          <button className="btn-primary flex-1" onClick={save} disabled={saving} data-testid="digest-save">
            {saving ? <Spinner /> : <Check className="w-4 h-4" />} Save
          </button>
          <button className="btn-ghost flex-1" onClick={sendTestDigest} disabled={busy} data-testid="digest-test">
            <Send className="w-4 h-4" /> Send test
          </button>
        </div>
      </div>

      {/* Data */}
      <div className="card p-5 mb-4">
        <h2 className="font-display text-lg text-ink mb-4">Your data</h2>
        <button className="btn-ghost w-full mb-2" onClick={exportJson} disabled={busy} data-testid="export-json">
          <Download className="w-4 h-4" /> Export profiles & history (JSON)
        </button>
      </div>

      {/* Danger */}
      <div className="card p-5 border-rose/30">
        <h2 className="font-display text-lg text-ink mb-1">Danger zone</h2>
        <p className="text-sm text-muted mb-4">Permanently delete your account and all associated data.</p>
        <button className="btn-ghost w-full mb-2" onClick={() => { logout(); navigate("/login"); }} data-testid="account-logout">
          <LogOut className="w-4 h-4" /> Log out
        </button>
        <button className="btn-danger w-full" onClick={deleteAccount} data-testid="delete-account">
          <Trash2 className="w-4 h-4" /> Delete account
        </button>
      </div>
    </div>
  );
}
